// Ad-attribution helper — captures UTM + click-id params on landing, stores
// the FIRST-TOUCH attribution in localStorage so downstream conversion events
// (signup, book-a-call, checkout) can be tied back to the ad that brought the
// visitor in — even if that happened hours/days earlier.
//
// Storage key: `cb_attr` — a JSON blob (see AD_ATTR_KEYS + timestamp/landing).
// First-touch policy: once written, we DO NOT overwrite unless the visitor
// arrives with a NEW `gclid`/`utm_source` that differs from the stored one.
// This gives us clean "first touch" attribution which is what most SMB ad
// spend accounting cares about; last-touch can still be derived server-side
// from the raw pageview stream if needed.

export const AD_ATTR_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid', 'msclkid',
];

const STORAGE_KEY = 'cb_attr';

/** Read the ad-attribution params currently present in the URL. */
export function readAdParamsFromUrl(search = window.location.search) {
  const p = new URLSearchParams(search);
  const out = {};
  for (const k of AD_ATTR_KEYS) {
    const v = p.get(k);
    if (v) out[k] = v.slice(0, 200);
  }
  return out;
}

/** Get the stored first-touch attribution blob (or null). */
export function getStoredAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Merge the URL params into first-touch storage.
 * Returns the effective attribution for THIS pageview (URL params, else stored).
 */
export function captureAdAttribution({ landingPath }) {
  const urlParams = readAdParamsFromUrl();
  const stored = getStoredAttribution();
  const hasFreshSignal = !!(urlParams.gclid || urlParams.utm_source || urlParams.fbclid || urlParams.msclkid);
  // Never overwrite the first-touch unless the visitor arrived from a
  // meaningfully different ad campaign (new gclid or new utm_source).
  const shouldWrite = hasFreshSignal && (
    !stored ||
    (urlParams.gclid && urlParams.gclid !== stored.gclid) ||
    (urlParams.utm_source && urlParams.utm_source !== stored.utm_source)
  );
  if (shouldWrite) {
    try {
      const now = new Date().toISOString();
      const blob = {
        ...urlParams,
        landing_path: landingPath || window.location.pathname,
        landed_at: now,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
      return blob;
    } catch {
      /* ignore quota / disabled storage */
    }
  }
  // Return URL params (this pageview's context) OR fall back to stored.
  return hasFreshSignal ? { ...urlParams, landing_path: landingPath } : (stored || {});
}

/** Get attribution to send with a conversion event (signup, booking, etc.). */
export function getAttributionForConversion() {
  return getStoredAttribution() || {};
}
